import React, { useEffect, useMemo } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Code,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { FiClock } from "react-icons/fi";
import { skipToken } from "@reduxjs/toolkit/query";
import { openapi } from "../../openapi";

type ScheduleDay = "Sun" | "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat";

type ScheduleFormValues = {
  enabled: boolean;
  days: ScheduleDay[];
  hour: number | "";
  minute: number | "";
  timezone: string;
  pageSize: number | "";
  maxRows: number | "";
};

type Props = {
  projectNo: number;
  disabled?: boolean;
};

const dayOptions: Array<{ value: ScheduleDay; label: string }> = [
  { value: "Sun", label: "Søn" },
  { value: "Mon", label: "Man" },
  { value: "Tue", label: "Tir" },
  { value: "Wed", label: "Ons" },
  { value: "Thu", label: "Tor" },
  { value: "Fri", label: "Fre" },
  { value: "Sat", label: "Lør" },
];

const timezoneOptions = [
  { value: "W. Europe Standard Time", label: "Norge/Sverige (W. Europe Standard Time)" },
  { value: "UTC", label: "UTC" },
];

const numberOrDefault = (value: number | "", fallback: number) =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const numberOrNull = (value: number | "") =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

function buildCronExpression(values: ScheduleFormValues): string {
  const dayOrder: ScheduleDay[] = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const days = Array.from(new Set(values.days)).sort(
    (a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b)
  );
  const startIndex = dayOrder.indexOf(days[0]);
  const contiguous = days.every((day, index) => day === dayOrder[(startIndex + index) % dayOrder.length]);
  const dayPart =
    days.length === dayOrder.length
      ? "*"
      : contiguous && days.length > 1
        ? `${days[0]}-${days[days.length - 1]}`
        : days.join(",");

  return `0 ${numberOrDefault(values.minute, 0)} ${numberOrDefault(values.hour, 2)} * * ${dayPart}`;
}

export const DrofusScheduleButton: React.FC<Props> = ({ projectNo, disabled = false }) => {
  const [opened, { open, close }] = useDisclosure(false);
  const queryArg = Number.isFinite(projectNo) && projectNo > 0 ? { projectNo } : skipToken;
  const { data, isFetching, refetch } =
    openapi.endpoints.getMultiplacerDrofusInterfaceOccurrencesSyncSchedule.useQuery(queryArg);
  const [updateSchedule, { isLoading: isSaving }] =
    openapi.endpoints.postMultiplacerDrofusInterfaceOccurrencesSyncSchedule.useMutation();

  const form = useForm<ScheduleFormValues>({
    initialValues: {
      enabled: false,
      days: ["Sun", "Mon", "Tue", "Wed", "Thu"],
      hour: 2,
      minute: 0,
      timezone: "W. Europe Standard Time",
      pageSize: 1000,
      maxRows: "",
    },
    validate: {
      days: (value) => (value.length === 0 ? "Velg minst én ukedag" : null),
      hour: (value) => {
        if (value === "") return "Sett time";
        if (value < 0 || value > 23) return "Må være mellom 0 og 23";
        return null;
      },
      minute: (value) => {
        if (value === "") return "Sett minutt";
        if (value < 0 || value > 59) return "Må være mellom 0 og 59";
        return null;
      },
      pageSize: (value) => {
        if (value === "") return "Sett page size";
        if (value < 100 || value > 2000) return "Må være mellom 100 og 2000";
        return null;
      },
      maxRows: (value) => {
        if (value === "") return null;
        if (value < 1 || value > 500000) return "Må være mellom 1 og 500000";
        return null;
      },
    },
  });

  useEffect(() => {
    if (!data) return;
    form.setValues({
      enabled: data.enabled,
      days: data.days as ScheduleDay[],
      hour: data.hour,
      minute: data.minute,
      timezone: data.timezone,
      pageSize: data.pageSize,
      maxRows: data.maxRows ?? "",
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const cronExpression = useMemo(() => buildCronExpression(form.values), [form.values]);

  const body = useMemo(
    () => ({
      projectNo,
      schedule: {
        enabled: form.values.enabled,
        days: form.values.days,
        hour: numberOrDefault(form.values.hour, 2),
        minute: numberOrDefault(form.values.minute, 0),
        timezone: form.values.timezone,
        pageSize: numberOrDefault(form.values.pageSize, 1000),
        maxRows: numberOrNull(form.values.maxRows),
      },
    }),
    [form.values, projectNo]
  );

  const handleSubmit = form.onSubmit(async () => {
    try {
      const result = await updateSchedule({ body }).unwrap();
      notifications.show({
        color: "green",
        title: "Kjøreplan lagret",
        message: result.enabled
          ? `Forekomstjobben er satt til ${result.cronExpression}.`
          : "Automatisk forekomstjobb er slått av.",
      });
      await refetch();
      close();
    } catch (error) {
      notifications.show({
        color: "red",
        title: "Kunne ikke lagre kjøreplan",
        message: error instanceof Error ? error.message : "Se konsollen eller backend-loggene.",
      });
    }
  });

  return (
    <>
      <Button
        size="xs"
        radius="sm"
        variant={data?.enabled ? "light" : "subtle"}
        color={data?.enabled ? "green" : "gray"}
        leftSection={<FiClock size={14} />}
        disabled={disabled}
        loading={isFetching && !data}
        onClick={open}
      >
        Kjøreplan
      </Button>

      <Modal opened={opened} onClose={close} title="Kjøreplan for dRofus-forekomster" size="lg" centered>
        <form onSubmit={handleSubmit}>
          <Stack gap="sm">
            <Alert color="blue" variant="light" radius="sm">
              En sentral timer kan lese denne prosjektinnstillingen og starte jobb når planen
              treffer. Azure Functions-cron for valgt tid vises under.
            </Alert>

            <Switch
              label="Kjør automatisk"
              description="Når aktivert kan scheduler starte forekomstjobben etter denne planen."
              {...form.getInputProps("enabled", { type: "checkbox" })}
            />

            <Checkbox.Group label="Ukedager" {...form.getInputProps("days")}>
              <Group gap="xs" mt={4}>
                {dayOptions.map((day) => (
                  <Checkbox key={day.value} value={day.value} label={day.label} />
                ))}
              </Group>
            </Checkbox.Group>

            <Group grow align="flex-start">
              <NumberInput
                label="Time"
                min={0}
                max={23}
                allowDecimal={false}
                {...form.getInputProps("hour")}
              />
              <NumberInput
                label="Minutt"
                min={0}
                max={59}
                allowDecimal={false}
                {...form.getInputProps("minute")}
              />
            </Group>

            <Select
              label="Timezone"
              data={timezoneOptions}
              searchable
              allowDeselect={false}
              {...form.getInputProps("timezone")}
            />

            <Group grow align="flex-start">
              <NumberInput
                label="Page size"
                description="Antall rader per request mot dRofus."
                min={100}
                max={2000}
                allowDecimal={false}
                {...form.getInputProps("pageSize")}
              />
              <NumberInput
                label="Maks rader"
                description="Tomt felt betyr ingen maksgrense."
                min={1}
                max={500000}
                allowDecimal={false}
                {...form.getInputProps("maxRows")}
              />
            </Group>

            <Stack gap={4}>
              <Text size="sm" fw={600}>
                Cron
              </Text>
              <Code block>{cronExpression}</Code>
            </Stack>

            <Group justify="flex-end" gap="xs">
              <Button size="xs" variant="subtle" radius="sm" onClick={close}>
                Avbryt
              </Button>
              <Button size="xs" radius="sm" type="submit" loading={isSaving}>
                Lagre
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
};
